type OverlayPoint = { x: number; y: number };

type GeneratorConfig = {
    seed?: number;
    version?: number;
    elevation_scale?: number;
    moisture_scale?: number;
    detail_scale?: number;
    river_scale?: number;
    sea_level?: number;
    beach_band?: number;
    hill_level?: number;
    mountain_level?: number;
    high_mountain_level?: number;
    desert_moisture?: number;
    forest_moisture?: number;
    marsh_moisture?: number;
    tall_grass_moisture?: number;
    river_threshold?: number;
};

type PathOverlay = {
    tag?: string;
    terrain?: number | null;
    width: number;
    points: OverlayPoint[];
    priority?: number;
};

type PolygonOverlay = {
    tag?: string;
    terrain?: number | null;
    points: OverlayPoint[];
    priority?: number;
};

type StampOverlay = {
    tag?: string;
    terrain?: number | null;
    x: number;
    y: number;
    radius: number;
    priority?: number;
};

type PaintMask = {
    x: number;
    y: number;
    cell_size: number;
    cols: number;
    rows: number;
    cells: number[];
    priority?: number;
};

type Overlays = {
    paths?: PathOverlay[];
    polygons?: PolygonOverlay[];
    stamps?: StampOverlay[];
    paint_masks?: PaintMask[];
};

function withGeneratorDefaults(generator: GeneratorConfig | null | undefined): Required<GeneratorConfig> {
    return {
        seed: generator?.seed ?? 1337,
        version: generator?.version ?? 1,
        elevation_scale: generator?.elevation_scale ?? 0.000055,
        moisture_scale: generator?.moisture_scale ?? 0.00008,
        detail_scale: generator?.detail_scale ?? 0.00022,
        river_scale: generator?.river_scale ?? 0.00017,
        sea_level: generator?.sea_level ?? 0.31,
        beach_band: generator?.beach_band ?? 0.035,
        hill_level: generator?.hill_level ?? 0.58,
        mountain_level: generator?.mountain_level ?? 0.72,
        high_mountain_level: generator?.high_mountain_level ?? 0.84,
        desert_moisture: generator?.desert_moisture ?? 0.24,
        forest_moisture: generator?.forest_moisture ?? 0.57,
        marsh_moisture: generator?.marsh_moisture ?? 0.78,
        tall_grass_moisture: generator?.tall_grass_moisture ?? 0.44,
        river_threshold: generator?.river_threshold ?? 0.905,
    };
}

function fade(t: number): number {
    return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function hash2(seed: number, x: number, y: number, salt: number): number {
    let h = (seed >>> 0)
        ^ Math.imul(x | 0, 374761393)
        ^ Math.imul(y | 0, 668265263)
        ^ Math.imul(salt | 0, 362437);
    h = Math.imul((h ^ (h >>> 13)) >>> 0, 1274126177);
    const value = (h ^ (h >>> 16)) >>> 0;
    return value / 0xffffffff;
}

function valueNoise(seed: number, x: number, y: number, salt: number): number {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const x1 = x0 + 1;
    const y1 = y0 + 1;
    const tx = fade(x - x0);
    const ty = fade(y - y0);

    const v00 = hash2(seed, x0, y0, salt);
    const v10 = hash2(seed, x1, y0, salt);
    const v01 = hash2(seed, x0, y1, salt);
    const v11 = hash2(seed, x1, y1, salt);

    const nx0 = lerp(v00, v10, tx);
    const nx1 = lerp(v01, v11, tx);
    return lerp(nx0, nx1, ty);
}

function fbm(seed: number, x: number, y: number, baseScale: number, octaves: number, salt: number): number {
    let total = 0;
    let amplitude = 1;
    let frequency = baseScale;
    let norm = 0;
    for (let octave = 0; octave < octaves; octave++) {
        total += valueNoise(seed, x * frequency, y * frequency, salt + octave) * amplitude;
        norm += amplitude;
        amplitude *= 0.5;
        frequency *= 2;
    }
    return norm <= 0 ? 0 : total / norm;
}

function pointInPolygon(px: number, py: number, points: OverlayPoint[]): boolean {
    if (points.length < 3) return false;
    let inside = false;
    let j = points.length - 1;
    for (let i = 0; i < points.length; i++) {
        const pi = points[i];
        const pj = points[j];
        const intersects = ((pi.y > py) !== (pj.y > py))
            && (px < ((pj.x - pi.x) * (py - pi.y)) / Math.max(Math.abs(pj.y - pi.y), 1e-9) + pi.x);
        if (intersects) inside = !inside;
        j = i;
    }
    return inside;
}

function pointSegmentDistance(px: number, py: number, a: OverlayPoint, b: OverlayPoint): number {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;
    if (lenSq <= 1e-9) {
        const dx = px - a.x;
        const dy = py - a.y;
        return Math.sqrt(dx * dx + dy * dy);
    }
    const t = Math.max(0, Math.min(1, (((px - a.x) * abx) + ((py - a.y) * aby)) / lenSq));
    const projX = a.x + abx * t;
    const projY = a.y + aby * t;
    const dx = px - projX;
    const dy = py - projY;
    return Math.sqrt(dx * dx + dy * dy);
}

function classifyBaseTerrain(generator: Required<GeneratorConfig>, x: number, y: number): number {
    const elevationLarge = fbm(generator.seed, x, y, generator.elevation_scale, 4, 11);
    const elevationDetail = fbm(generator.seed, x, y, generator.detail_scale, 3, 29);
    const elevation = Math.max(0, Math.min(1, elevationLarge * 0.74 + elevationDetail * 0.26));

    const moistureLarge = fbm(generator.seed, x, y, generator.moisture_scale, 4, 53);
    const moistureDetail = fbm(generator.seed, x, y, generator.detail_scale * 0.7, 3, 71);
    const moisture = Math.max(0, Math.min(1, moistureLarge * 0.7 + moistureDetail * 0.3));

    const river = fbm(generator.seed, x, y, generator.river_scale, 5, 97);

    if (elevation < generator.sea_level) return 11;
    if (elevation < generator.sea_level + generator.beach_band) return 5;
    if (elevation >= generator.high_mountain_level) return 10;
    if (elevation >= generator.mountain_level) return 9;
    if (river >= generator.river_threshold && elevation < generator.mountain_level) {
        return river >= generator.river_threshold + 0.035 ? 8 : 7;
    }
    if (elevation >= generator.hill_level) return 2;
    if (moisture <= generator.desert_moisture) return 3;
    if (moisture >= generator.marsh_moisture) return 6;
    if (moisture >= generator.forest_moisture) return 1;
    if (moisture >= generator.tall_grass_moisture) return 4;
    return 0;
}

function applyOverlays(overlays: Overlays | null | undefined, x: number, y: number, base: number): number {
    const normalized = overlays || {};
    let bestPriority = Number.NEGATIVE_INFINITY;
    let terrain = base;

    for (const mask of normalized.paint_masks || []) {
        const localX = x - mask.x;
        const localY = y - mask.y;
        if (localX < 0 || localY < 0) continue;
        const col = Math.floor(localX / mask.cell_size);
        const row = Math.floor(localY / mask.cell_size);
        if (col < 0 || row < 0 || col >= mask.cols || row >= mask.rows) continue;
        const idx = row * mask.cols + col;
        const value = mask.cells[idx];
        if (value == null) continue;
        if ((mask.priority ?? 0) >= bestPriority) {
            bestPriority = mask.priority ?? 0;
            terrain = value;
        }
    }

    for (const polygon of normalized.polygons || []) {
        if (polygon.terrain == null) continue;
        if ((polygon.priority ?? 0) < bestPriority) continue;
        if (pointInPolygon(x, y, polygon.points || [])) {
            bestPriority = polygon.priority ?? 0;
            terrain = polygon.terrain;
        }
    }

    for (const path of normalized.paths || []) {
        if (path.terrain == null || !Array.isArray(path.points) || path.points.length < 2) continue;
        if ((path.priority ?? 0) < bestPriority) continue;
        let matched = false;
        for (let i = 0; i < path.points.length - 1; i++) {
            if (pointSegmentDistance(x, y, path.points[i], path.points[i + 1]) <= path.width * 0.5) {
                matched = true;
                break;
            }
        }
        if (matched) {
            bestPriority = path.priority ?? 0;
            terrain = path.terrain;
        }
    }

    for (const stamp of normalized.stamps || []) {
        if (stamp.terrain == null) continue;
        if ((stamp.priority ?? 0) < bestPriority) continue;
        const dx = x - stamp.x;
        const dy = y - stamp.y;
        if (dx * dx + dy * dy <= stamp.radius * stamp.radius) {
            bestPriority = stamp.priority ?? 0;
            terrain = stamp.terrain;
        }
    }

    return terrain;
}

function proceduralCellTerrain(manifest: any, worldX: number, worldY: number): number {
    const generator = withGeneratorDefaults(manifest.generator);
    const base = classifyBaseTerrain(generator, worldX, worldY);
    return applyOverlays(manifest.overlays, worldX, worldY, base);
}

function buildProceduralChunk(manifest: any, cx: number, cy: number): any {
    const tg = manifest.terrain_grid || {};
    const cellSize = tg.cell_size || 100;
    const chunkCellCols = tg.chunk_cell_cols || 32;
    const chunkCellRows = tg.chunk_cell_rows || 32;
    const totalCols = tg.cols || Math.max(0, Math.round((manifest.width || 0) / cellSize));
    const totalRows = tg.rows || Math.max(0, Math.round((manifest.height || 0) / cellSize));
    const cols = Math.max(0, Math.min(chunkCellCols, totalCols - cx * chunkCellCols));
    const rows = Math.max(0, Math.min(chunkCellRows, totalRows - cy * chunkCellRows));
    const cells: number[] = [];
    const counts = new Array<number>(12).fill(0);

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const worldCol = cx * chunkCellCols + col;
            const worldRow = cy * chunkCellRows + row;
            const worldX = (worldCol + 0.5) * cellSize;
            const worldY = (worldRow + 0.5) * cellSize;
            const terrain = proceduralCellTerrain(manifest, worldX, worldY);
            cells.push(terrain);
            counts[terrain] = (counts[terrain] || 0) + 1;
        }
    }

    let dominantTerrain = 0;
    let dominantCount = -1;
    for (let terrain = 0; terrain < counts.length; terrain++) {
        if (counts[terrain] > dominantCount) {
            dominantCount = counts[terrain];
            dominantTerrain = terrain;
        }
    }

    return {
        x: cx,
        y: cy,
        cols,
        rows,
        cell_size: cellSize,
        cells,
        dominant_terrain: dominantTerrain,
    };
}

function buildProceduralChunkMeta(manifest: any): any[] {
    const tg = manifest.terrain_grid || {};
    const cellSize = tg.cell_size || 100;
    const cols = tg.cols || Math.max(0, Math.round((manifest.width || 0) / cellSize));
    const rows = tg.rows || Math.max(0, Math.round((manifest.height || 0) / cellSize));
    const chunkCellCols = tg.chunk_cell_cols || 32;
    const chunkCellRows = tg.chunk_cell_rows || 32;
    const chunkCols = tg.chunk_cols || Math.ceil(cols / Math.max(1, chunkCellCols));
    const chunkRows = tg.chunk_rows || Math.ceil(rows / Math.max(1, chunkCellRows));
    const metas: any[] = [];
    for (let cy = 0; cy < chunkRows; cy++) {
        for (let cx = 0; cx < chunkCols; cx++) {
            const chunk = buildProceduralChunk(manifest, cx, cy);
            metas.push({
                x: cx,
                y: cy,
                cols: chunk.cols,
                rows: chunk.rows,
                dominant_terrain: chunk.dominant_terrain,
            });
        }
    }
    return metas;
}

export function isProceduralManifest(manifest: any): boolean {
    return !!manifest && (
        manifest.terrain_mode === 'procedural'
        || manifest.world_type === 'procedural'
        || !!manifest.generator
    );
}

export function createProceduralMapRuntime(mapName: string, manifest: any): any {
    const tg = manifest.terrain_grid || {};
    const cellSize = tg.cell_size || 100;
    const cols = tg.cols || Math.max(0, Math.round((manifest.width || 0) / cellSize));
    const rows = tg.rows || Math.max(0, Math.round((manifest.height || 0) / cellSize));
    const chunkCellCols = tg.chunk_cell_cols || 32;
    const chunkCellRows = tg.chunk_cell_rows || 32;
    const chunkCols = tg.chunk_cols || Math.ceil(cols / Math.max(1, chunkCellCols));
    const chunkRows = tg.chunk_rows || Math.ceil(rows / Math.max(1, chunkCellRows));
    const chunks = Array.isArray(tg.chunks) && tg.chunks.length > 0 ? tg.chunks : buildProceduralChunkMeta({
        ...manifest,
        terrain_grid: {
            ...tg,
            cols,
            rows,
            chunk_cols: chunkCols,
            chunk_rows: chunkRows,
            chunk_cell_cols: chunkCellCols,
            chunk_cell_rows: chunkCellRows,
        },
    });
    const chunkIndex = new Map<string, any>();
    for (const chunk of chunks) {
        chunkIndex.set(`${chunk.x},${chunk.y}`, chunk);
    }
    const loadedChunks = new Map<string, any>();
    const pendingLoads = new Map<string, Promise<any>>();
    const normalizedManifest = {
        ...manifest,
        terrain_mode: 'procedural',
        terrain_grid: {
            ...tg,
            cell_size: cellSize,
            cols,
            rows,
            chunk_cols: chunkCols,
            chunk_rows: chunkRows,
            chunk_cell_cols: chunkCellCols,
            chunk_cell_rows: chunkCellRows,
            chunks,
        },
    };

    return {
        name: manifest.name || mapName,
        is_siege: !!manifest.is_siege,
        terrain_mode: 'procedural',
        width: manifest.width,
        height: manifest.height,
        teams: manifest.teams || [{ name: 'Team 1' }, { name: 'Team 2' }],
        spawn_zones: manifest.spawn_zones || [],
        structures: manifest.structures || [],
        terrain_grid: normalizedManifest.terrain_grid,
        cell_size: cellSize,
        terrain_cols: cols,
        terrain_rows: rows,
        terrain_cells: [],
        chunked: true,
        procedural: true,
        generator: normalizedManifest.generator,
        overlays: normalizedManifest.overlays || {},
        road_graph: normalizedManifest.road_graph || null,
        terrain_chunk_cols: chunkCols,
        terrain_chunk_rows: chunkRows,
        terrain_chunk_cell_cols: chunkCellCols,
        terrain_chunk_cell_rows: chunkCellRows,
        terrain_chunks: chunks,
        getChunkMeta: (cx: number, cy: number) => chunkIndex.get(`${cx},${cy}`) || null,
        getLoadedChunk: (cx: number, cy: number) => loadedChunks.get(`${cx},${cy}`) || null,
        loadChunk: async (cx: number, cy: number) => {
            const key = `${cx},${cy}`;
            const existing = loadedChunks.get(key);
            if (existing) return existing;
            if (pendingLoads.has(key)) return pendingLoads.get(key);
            if (!chunkIndex.has(key)) return null;
            const request = Promise.resolve().then(() => {
                const chunk = buildProceduralChunk(normalizedManifest, cx, cy);
                loadedChunks.set(key, chunk);
                pendingLoads.delete(key);
                return chunk;
            }).catch((error) => {
                pendingLoads.delete(key);
                console.warn('[ProceduralMap] Failed to generate chunk:', mapName, key, error);
                return null;
            });
            pendingLoads.set(key, request);
            return request;
        },
    };
}

